/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import * as Styles from './CommonForm.styles';
import useLocale from '@/hooks/useLocale/useLocale';
import {
  Alert,
  Button, Checkbox, IconLoading, SelectField, TextField,
} from 'gatorcomponents';
import { useDispatch, useSelector } from 'react-redux';
import { preChatActions } from '@/redux/modules';
import { COUNTRY } from '@/config';
import { isBRServer } from '@/utils/Validators/validation';
import { sidebarAnalytics } from '@/analytics/support/sidebar';

export const CommonForm = ({
  submitForm,
  opening,
  selectedProductOption,
  ticketType,
}) => {
  const [agreed, setAgreed] = useState(false);
  const [review, setReview] = useState(false);
  const [file, setFile] = useState('');
  const [fileBinary, setFileBinary] = useState();
  const [description, setDescription] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [hasError, setHasError] = useState(false);

  const dispatch = useDispatch();
  const { productsList } = useSelector(state => state.preChat);
  const { email } = useSelector(state => state.summary);

  const hostingsProducts = useMemo(() => (productsList.hostings
    ? productsList.hostings
      .filter(hosting => hosting.status === 'Active' || hosting.status === 'Suspended')
      .map(product => ({
        value: product.id,
        label: product.domain ? `${product.name} (${product.domain})` : product.name,
        name: product.name,
      }))
    : []), [productsList.hostings]);

  const domainsProducts = useMemo(() => (productsList.domains
    ? productsList.domains
      .filter(domain => domain.status === 'Active' || domain.status === 'Suspended')
      .map(product => ({ value: product.id, label: product.name, name: product.type }))
    : []), [productsList.domains]);

  const productsListToSelect = useMemo(() => [...hostingsProducts, ...domainsProducts], [hostingsProducts, domainsProducts]);

  const { ticket: ticketsLocale } = useLocale();
  const { commonForm: commonFormLocale, names: ticketNamesLocale } = ticketsLocale.forms;

  const fetchProducts = useCallback(() => {
    if (!productsList.domains) {
      dispatch(preChatActions.productByStatus.request({ withCancelled: true, withPending: false }));
    }
  }, [dispatch, productsList.domains]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleFile = (e) => {
    setFileBinary(e.target.files[0]);
    setFile(e.currentTarget.value);
  };

  const handleDescription = (value) => {
    setDescription(value);
    setHasError(false);
  };

  const trackerGroup = useCallback(() => {
    switch (ticketType) {
      case 'financial':
        return 'CATEGORY_FINANCIAL';
      case 'hosting':
        return 'CATEGORY_HOSTING';
      case 'domain':
        return 'CATEGORY_DOMAIN';
      case 'email':
        return 'CATEGORY_EMAIL';
      case 'siteOffline':
        return 'CATEGORY_SITE_OFFLINE';
      case 'other':
        return 'CATEGORY_NOT_FOUND';
      default:
        break;
    }
  }, [ticketType]);

  const handleTagSelection = useCallback(() => {
    switch (ticketType) {
      case 'financial':
        return isBRServer ? 'whmcs_form_duvida_financeira' : 'whmcs_form_duda_financiera';
      case 'hosting':
        return isBRServer ? 'whmcs_form_duvida_hospedagem' : 'whmcs_form_duda_hospedaje';
      case 'domain':
        return isBRServer ? 'whmcs_form_duvida_dominio' : 'whmcs_form_duda_dominio';
      case 'email':
        return isBRServer ? 'whmcs_form_duvida_email' : 'whmcs_form_duda_correo';
      case 'siteOffline':
        return isBRServer ? 'whmcs_form_duvida_siteoffline' : 'whmcs_form_duda_sitiooffline';
      case 'other':
        return isBRServer ? 'whmcs_form_duvida_outros' : 'whmcs_form_duda_otros';
      default:
        break;
    }
  }, [ticketType]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (!description) {
      setHasError(true);
      return;
    }

    const tag = handleTagSelection();
    const productName = selectedProduct && productsListToSelect.find(product => product.label === selectedProduct.label).name.toLowerCase().replace(/ /g, '_');

    const data = {
      automation: false,
      product: selectedProduct ? `${selectedProduct.label} - id: ${selectedProduct.value}` : 'none',
      subject: selectedProductOption,
      files: [fileBinary],
      tags: [
        tag,
        productName,
      ],
      body: `
      ${commonFormLocale.category}: ${ticketNamesLocale[ticketType]}
      ${commonFormLocale.description}: ${description}
      ${commonFormLocale.product}: ${selectedProduct ? `${selectedProduct.label} - id: ${selectedProduct.value}` : 'none'}
      `,
    };

    sidebarAnalytics.newSupportAnalytics('SUPPORT', trackerGroup());
    submitForm && submitForm(data, true);
  }, [
    selectedProductOption,
    submitForm,
    fileBinary,
    description,
    handleTagSelection,
    trackerGroup,
    selectedProduct,
    ticketType,
    ticketNamesLocale,
    commonFormLocale,
    productsListToSelect,
  ]);

  return (
    <Styles.Form onSubmit={e => handleSubmit(e)}>
      <Styles.InputWrapper>
        <Styles.Info>
          {commonFormLocale.selectProduct}
        </Styles.Info>
        <Styles.SelectWrapper>
          <SelectField
            label={selectedProduct ? commonFormLocale.selectProductPlaceholder : ''}
            placeholder={commonFormLocale.selectProductPlaceholder}
            options={productsListToSelect}
            value={selectedProduct && selectedProduct.label}
            onClickSelect={option => setSelectedProduct(option)}
            testId="select-domain"
            positionAbsolute
          />
        </Styles.SelectWrapper>

        <Styles.Info maxWidth={375}>
          {commonFormLocale.info}
        </Styles.Info>

        <Styles.AlertWrapper>
          <Alert type="info" description={commonFormLocale.alert(email)} />
        </Styles.AlertWrapper>

        <TextField
          type="textarea"
          testId="description"
          placeholder=""
          label={commonFormLocale.describe}
          value={description}
          onChange={e => handleDescription(e.target.value)}
          inputHeight={185}
          helpText={hasError && commonFormLocale.required}
          status={hasError && 'error'}
        />

        <Styles.FileDescription>
          {commonFormLocale.screenShot}
        </Styles.FileDescription>

        <Styles.FileWrapper>
          <Styles.FileButton htmlFor="fileInput" file={!!file}>
            {(fileBinary && fileBinary.name) || commonFormLocale.sendFile}
          </Styles.FileButton>
          <Styles.FileInput
            type="file"
            id="fileInput"
            data-testid="fileInput"
            accept=".jpg,.jpeg,.png,.pdf"
            value={file}
            onChange={e => handleFile(e)}
          />
          <Styles.FileInputExtensions>
            {commonFormLocale.fileInputExtensions('.jpg, .jpeg, .png, .pdf')}
          </Styles.FileInputExtensions>
        </Styles.FileWrapper>
      </Styles.InputWrapper>

      <Styles.Divider />

      <Styles.Checkboxes>
        <Styles.CheckboxWrapper>
          <Styles.CheckboxLabel
            htmlFor="#agreed"
            onClick={() => setAgreed(!agreed)}
            data-testid="checkAgreed"
          >
            <Checkbox checked={agreed} id="agreed" />
            <Styles.Text>
              {commonFormLocale.checkbox(COUNTRY.toLowerCase())}
            </Styles.Text>
          </Styles.CheckboxLabel>
        </Styles.CheckboxWrapper>

        <Styles.CheckboxWrapper>
          <Styles.CheckboxLabel
            htmlFor="#review"
            onClick={() => setReview(!review)}
            data-testid="checkReview"
          >
            <Checkbox checked={review} id="review" />
            <Styles.Text>
              {commonFormLocale.review}
            </Styles.Text>
          </Styles.CheckboxLabel>
        </Styles.CheckboxWrapper>
      </Styles.Checkboxes>

      <Styles.ButtonWrapper>
        <Button
          size="large"
          label={opening ? <IconLoading /> : commonFormLocale.submitLabel}
          disabled={hasError || opening || !agreed || !review}
          type="submit"
          testId="submit"
        />
      </Styles.ButtonWrapper>
    </Styles.Form>
  );
};

export default CommonForm;
